import { create } from 'zustand'
import { User } from 'firebase/auth'
import { toast } from 'sonner'
import { DriverMaster } from '@/types/driverMaster'
import { Period, PeriodStatus, WizardStep } from '@/types/period'
import { PeriodService } from '@/services/periodService'
import { LockedSalaryDataService } from '@/services/lockedSalaryDataService'
import { DriverMasterService } from '@/services/driverMasterService'

interface PeriodStore {
  // State
  periods: Period[]
  currentPeriod: string | null  // Currently selected period (format: "yyyy-mm-01")
  loading: boolean
  error: string | null

  // Lock/unlock operation state
  lockingPeriod: boolean
  unlockingPeriod: boolean

  // Actions
  loadPeriods: (user: User) => Promise<void>
  setCurrentPeriod: (period: string | null) => void
  lockPeriod: (user: User, period: string) => Promise<void>
  unlockPeriod: (user: User, period: string, reason: string) => Promise<void>
  markStepComplete: (user: User, periodId: string, step: WizardStep) => Promise<void>

  // Getters
  getPeriodStatus: (period: string) => PeriodStatus | null
  getPeriod: (periodId: string) => Period | undefined
  isCurrentPeriodLocked: () => boolean

  // UI state
  clearError: () => void
}

export const usePeriodStore = create<PeriodStore>((set, get) => ({
  // Initial state
  periods: [],
  currentPeriod: null,
  loading: false,
  error: null,
  lockingPeriod: false,
  unlockingPeriod: false,

  /**
   * Load all periods for the user
   */
  loadPeriods: async (user: User) => {
    try {
      set({ loading: true, error: null })

      const periods = await PeriodService.listPeriods(user)

      set({
        periods,
        loading: false
      })
    } catch (error) {
      console.error('Failed to load periods:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ladda perioder'
      set({
        error: errorMessage,
        loading: false
      })
      toast.error(errorMessage)
    }
  },

  /**
   * Set the currently selected period
   * This is used globally across the app to filter data by period
   */
  setCurrentPeriod: (period: string | null) => {
    set({ currentPeriod: period })
  },

  /**
   * Lock a period - creates snapshots and sets period to locked
   */
  lockPeriod: async (user: User, periodId: string) => {
    try {
      set({ lockingPeriod: true, error: null })

      // Get period to check for excluded drivers
      const period = await PeriodService.getPeriod(user, periodId)
      const excludedDriverIds = new Set(period?.excludedDriverIds || [])

      // Step 1: Lock the period (just sets the flag)
      await PeriodService.lockPeriod(user, periodId)

      // Step 2: Get all driver IDs that have earnings in this period
      // We need to create snapshots for each driver
      const { EarningsService } = await import('@/services/earningsService')
      const earnings = await EarningsService.getEarningsByPeriod(user, periodId)

      // Get unique driver IDs from earnings
      const allDriverIds = [...new Set(earnings.map(e => e.driverId))]

      if (allDriverIds.length === 0) {
        throw new Error('Inga förare hittades för denna period')
      }

      // Filter out inactive drivers (deleted/merged) and excluded drivers
      const drivers = await Promise.all(
        allDriverIds.map(id => DriverMasterService.getDriver(user, id))
      )
      const driverIds = drivers
        .filter((d): d is DriverMaster => d !== null && d.isActive && !excludedDriverIds.has(d.id))
        .map(d => d.id)

      const skippedInactive = allDriverIds.length - drivers.filter(d => d !== null && d.isActive).length
      const skippedExcluded = excludedDriverIds.size
      if (skippedInactive > 0) {
        console.warn(`Skipped ${skippedInactive} inactive driver(s) during period lock - earnings exist for deleted/merged drivers`)
      }
      if (skippedExcluded > 0) {
        console.log(`Excluded ${skippedExcluded} driver(s) from period lock as per user selection`)
      }

      if (driverIds.length === 0) {
        throw new Error('Inga aktiva förare hittades för denna period')
      }

      // Step 3: Create snapshot for each driver
      const snapshotPromises = driverIds.map(driverId =>
        LockedSalaryDataService.createSnapshot(user, driverId, periodId)
          .catch(err => {
            console.error(`Failed to create snapshot for driver ${driverId}:`, err)
            return null // Don't fail the whole operation if one driver fails
          })
      )

      const snapshots = await Promise.all(snapshotPromises)
      const successCount = snapshots.filter(s => s !== null).length
      const failedCount = driverIds.length - successCount

      // Step 4: Reload periods to get updated status
      await get().loadPeriods(user)

      set({ lockingPeriod: false })

      if (failedCount > 0) {
        toast.warning(`Period låst! ${successCount} av ${driverIds.length} förare sparades. ${failedCount} misslyckades.`)
      } else {
        toast.success(`Period låst! ${successCount} förare sparade.`)
      }
    } catch (error) {
      console.error('Failed to lock period:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att låsa perioden'
      set({
        error: errorMessage,
        lockingPeriod: false
      })
      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Unlock a period - deactivates snapshots and sets period to draft
   */
  unlockPeriod: async (user: User, periodId: string, reason: string) => {
    try {
      set({ unlockingPeriod: true, error: null })

      // Step 1: Deactivate all active snapshots for this period
      const deactivatedCount = await LockedSalaryDataService.deactivateAllForPeriod(
        user,
        periodId,
        reason
      )

      // Step 2: Unlock the period
      await PeriodService.unlockPeriod(user, periodId, reason)

      // Step 3: Reload periods to get updated status
      await get().loadPeriods(user)

      set({ unlockingPeriod: false })

      toast.success(`Period upplåst! ${deactivatedCount} snapshots inaktiverade.`)
    } catch (error) {
      console.error('Failed to unlock period:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att låsa upp perioden'
      set({
        error: errorMessage,
        unlockingPeriod: false
      })
      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Mark a wizard step as complete for a period
   */
  markStepComplete: async (user: User, periodId: string, step: WizardStep) => {
    try {
      const period = get().getPeriod(periodId)
      const currentCompleted = period?.completedSteps || []

      // Don't add if already completed
      if (currentCompleted.includes(step)) {
        return
      }

      const updatedSteps = [...currentCompleted, step]
      await PeriodService.updatePeriod(user, periodId, { completedSteps: updatedSteps })

      // Update local state
      set(state => ({
        periods: state.periods.map(p =>
          p.id === periodId
            ? { ...p, completedSteps: updatedSteps }
            : p
        )
      }))
    } catch (error) {
      console.error('Failed to mark step complete:', error)
      // Don't show toast for this - it's not critical
    }
  },

  /**
   * Get the status of a specific period
   * Returns null if period doesn't exist (treated as draft)
   */
  getPeriodStatus: (periodId: string) => {
    const { periods } = get()
    const period = periods.find(p => p.id === periodId)
    return period?.status || null
  },

  /**
   * Get a specific period by ID
   */
  getPeriod: (periodId: string) => {
    const { periods } = get()
    return periods.find(p => p.id === periodId)
  },

  /**
   * Check if the currently selected period is locked
   */
  isCurrentPeriodLocked: () => {
    const { currentPeriod, periods } = get()
    if (!currentPeriod) return false

    const period = periods.find(p => p.id === currentPeriod)
    return period?.status === 'locked'
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null })
  },
}))
